import WorkModalShell from '@/components/common/WorkModalShell';
import BrandLogo from '@/components/common/BrandLogo';
import { brandConfig } from '@/config/brandConfig';
import type { PurchaseDetail } from '@/hooks/usePurchases';
import {
  purchaseTotals,
  purchaseVariantDetails,
  saleMoney,
  saleStatusLabel,
  purchaseBalance,
  purchaseStatusLabel,
} from '@klothiq/domain';
import { printElementById } from '@/utils/printUtils';

export default function PurchaseOrderPreview({
  purchase: p,
  onClose,
}: {
  purchase: PurchaseDetail;
  onClose: () => void;
}) {
  const totals = purchaseTotals(p.purchase_items, Number(p.other_charges));
  const summary: Array<[string, number | null]> = [
    ...(p.details_mode === 'amount_only'
      ? []
      : ([
          ['Subtotal', totals.subtotal],
          ['Total discount', totals.discount],
          ['Other charges', Number(p.other_charges)],
        ] as Array<[string, number]>)),
    ['Total', Number(p.total_amount)],
    ['Recorded payments', Number(p.paid_amount)],
    ['Supplier balance', purchaseBalance(p)],
  ];
  return (
    <WorkModalShell
      open
      onClose={onClose}
      title={p.workflow_mode === 'record_only' ? 'Purchase record' : 'Purchase order'}
      wide
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">
          {p.workflow_mode === 'record_only' ? 'Purchase record' : 'Purchase order'}
        </h2>
        <button onClick={onClose} className="min-h-11 rounded-lg border border-border px-3 text-sm">
          Close
        </button>
      </div>
      <div className="min-h-0 overflow-y-auto">
        <div
          id="purchase-order-print"
          className="space-y-4 rounded-xl bg-white p-4 text-slate-900 sm:p-6"
        >
          <header className="border-b border-slate-200 pb-4">
            <div className="mb-4 flex items-center gap-3">
              <BrandLogo className="h-10 w-10" />
              <div className="min-w-0">
                <p className="font-bold">{brandConfig.name}</p>
                <p className="text-xs text-slate-600">{brandConfig.tagline}</p>
              </div>
            </div>
            <h1 className="text-xl font-bold">
              {p.workflow_mode === 'record_only' ? 'Purchase record' : 'Purchase order'}{' '}
              {p.purchase_number}
            </h1>
            <p className="mt-2 text-sm">Supplier: {p.suppliers?.name || 'Supplier'}</p>
            <p className="text-sm">
              Date: {p.purchase_date} ·{' '}
              {p.workflow_mode === 'record_only'
                ? purchaseStatusLabel(p)
                : saleStatusLabel(p.status)}
            </p>
            {p.supplier_invoice_no && <p className="text-sm">Invoice: {p.supplier_invoice_no}</p>}
            <p className="mt-2 text-xs font-semibold">Purchase document — not proof of payment</p>
          </header>
          {p.purchase_items.map((i) => (
            <article
              key={i.id}
              className="break-inside-avoid border-b border-slate-200 pb-3 text-sm"
            >
              <p className="font-semibold">
                {i.description || i.product_variants?.products?.name || 'Product'}
              </p>
              <p className="text-xs">
                {[
                  purchaseVariantDetails(i).sku,
                  purchaseVariantDetails(i).size,
                  purchaseVariantDetails(i).color,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <div className="mt-2 flex flex-wrap justify-between gap-2">
                <span>Quantity: {i.quantity}</span>
                <span>Unit cost: {saleMoney(i.unit_cost)}</span>
                <span>Discount: {saleMoney(i.discount)}</span>
                <strong>Total: {saleMoney(i.line_total)}</strong>
              </div>
            </article>
          ))}
          <dl className="ml-auto max-w-xs space-y-2 text-sm">
            {summary.map(([label, amount]) => (
              <div key={String(label)} className="flex justify-between gap-4">
                <dt>{label}</dt>
                <dd className="font-semibold">
                  {amount === null ? 'Not tracked' : saleMoney(Number(amount))}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
      <button
        onClick={() => printElementById('purchase-order-print', `Purchase ${p.purchase_number}`)}
        className="mt-4 min-h-11 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"
      >
        Print order
      </button>
    </WorkModalShell>
  );
}
